import React, { useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import useNotifications from "../../../hooks/useNotifications";
import List from "components/UI/List";
import IconBell from "components/Icon/Bell";
import Link from "components/UI/Link";
import TimeAgo from "components/UI/Timeago";
import {
  Badge,
  IconContainer,
  NotificationBox,
  StyledDialog,
  ViewAllDiv
} from "./NotificationTray.styles";
import { getCompanyId } from "selector/userSelectors";

const NotificationTray = ({ className, companyId }) => {
  const notificationsNode = useRef(null);

  //TODO: Implement a NotificationContext and consume here to get a function to show errors to user
  const {
    notifications,
    loading,
    error,
    markAsRead,
    remove
  } = useNotifications({ companyId });
  const [state, setState] = useState({
    activeNotificationId: null,
    showDialog: false
  });

  const toggleNotifications = useCallback(() => {
    setState(state => ({
      activeNotificationId: null,
      showDialog: !state.showDialog
    }));
  }, []);

  const handleDialogOutsideClick = useCallback(event => {
    if (
      notificationsNode.current &&
      notificationsNode.current.contains(event.target)
    ) {
      return;
    }
    setState({ showDialog: false, activeNotificationId: null });
  }, []);

  const handleItemClick = useCallback(
    async notification => {
      const { id, read } = notification;
      setState(state => ({
        activeNotificationId: state.activeNotificationId !== id ? id : null
      }));

      if (!read) {
        try {
          await markAsRead(notification);
        } catch (err) {
          console.log(JSON.stringify(err));
        }
      }
    },
    [markAsRead]
  );

  const handleDeleteClick = useCallback(
    async (event, notification) => {
      // Needed, otherwise will trigger handleItemClick and mess with state
      event.stopPropagation();

      try {
        await remove(notification);
      } catch (err) {
        console.log(err);
      }
    },
    [remove]
  );

  const unreadCount = notifications
    ? notifications.reduce((acc, notif) => acc + Number(!notif.read), 0)
    : null;

  const badge = unreadCount ? (
    <Badge className="badge--floating">{unreadCount}</Badge>
  ) : null;

  const renderNotifications = () => {
    if (loading) {
      return "loading...";
    }
    if (error) {
      return error.message;
    }
    return notifications.map(notification => (
      <List.Item
        key={notification.id}
        onClick={() => handleItemClick(notification)}
        className={classNames("notification", {
          active: state.activeNotificationId === notification.id,
          read: notification.read
        })}
      >
        {notification.htmlContent ? (
          <div
            className="content"
            dangerouslySetInnerHTML={{ __html: notification.htmlContent }}
          ></div>
        ) : (
          <div className="content">
            {`${notification.title} ${notification.content}`}
          </div>
        )}

        {state.activeNotificationId === notification.id ? (
          <Link
            href="#"
            title="Remove this notification"
            onClick={event => handleDeleteClick(event, notification)}
          >
            Remove
          </Link>
        ) : (
          <TimeAgo className="timestamp">{notification.createdAt}</TimeAgo>
        )}
      </List.Item>
    ));
  };

  return (
    <React.Fragment>
      <IconContainer
        data-dialog="notification-dialog"
        onClick={toggleNotifications}
        innerRef={notificationsNode}
        title="Recent notifications"
      >
        <IconBell className={className} />

        {badge}
      </IconContainer>

      <StyledDialog
        show={state.showDialog}
        onOutsideClick={handleDialogOutsideClick}
        target="notification-dialog"
      >
        <NotificationBox>
          <List className="notif-list">{renderNotifications()}</List>
          <ViewAllDiv>
            <Link href="/notifications">View All</Link>
          </ViewAllDiv>
        </NotificationBox>
      </StyledDialog>
    </React.Fragment>
  );
};

NotificationTray.propTypes = {
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      content: PropTypes.string,
      htmlContent: PropTypes.string,
      read: PropTypes.bool.isRequired,
      createdAt: PropTypes.instanceOf(Date).isRequired
    })
  ),
  className: PropTypes.string
};

NotificationTray.defaultProps = {
  notifications: []
};

export default connect(state => ({ companyId: getCompanyId(state) }))(
  NotificationTray
);
